import OTPInput from "otp-input-react";
import React, { useState, useEffect } from "react";
import Loader from "../../../components/loader/loader";

import Snackbar from "../../../components/snackbar/snackbar";
import { verifyOtp, userLogin, selectApplicationContext, switchAccount } from "../login/auth.api";
import jwtDecode from "jwt-decode";
import "./otp.css";

const getApplicationDisplayName = (record) => {
  const refNo = String(record?.unique_application_ref_no || "").toUpperCase();

  if (refNo.startsWith("KSPACE-PREMISES_")) {
    return "KSPACE Premises";
  }

  if (
    refNo.startsWith("KSPACE-OWN_") ||
    refNo.startsWith("KSPACE-GENERAL_")
  ) {
    return "KSPACE - Own-Premises";
  }

  if (refNo.startsWith("KSPACE/E&IT/AFF/")) {
    return "AFFLIATED COMPANY";
  }

  return record?.type || "Application";
};

export default function Otp(props) {
  useEffect(() => {
    if (!props.phone) {
      props.history.push("/login");
    }
  }, []);

  const [OTP, setOTP] = useState("");
  const [submit, setSubmit] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showSelection, setShowSelection] = useState(false);
  const [records, setRecords] = useState([]);
  const [tempToken, setTempToken] = useState("");

  const activelocation = sessionStorage.getItem("next_path");
  const activePath = activelocation ?? "/dashboard";

  const completeLogin = (token) => {
    sessionStorage.setItem("token", token);
    props.login(token);

    let targetPath = activePath;

    // Only apply default role-based redirects if NO specific next_path was saved
    // This ensures deep-linking works (e.g., APPLY NOW → login → /training-reg)
    if (!activelocation) {
      try {
        const userData = jwtDecode(token);
        const userType = Number(userData?.user_type || userData?.user?.user_type || userData?.sub);
        const dashboardRoles = [0, 4, 5, 6, 8, 9, 10, 24];
        const publicRoles = [21, 27];

        if (dashboardRoles.includes(userType)) {
          targetPath = "/dashboard";
        } else if (publicRoles.includes(userType)) {
          targetPath = "/";
        }
      } catch (e) {
        console.error("Token decoding error:", e);
      }
    }

    // Clear next_path after use to prevent stale redirects
    sessionStorage.removeItem("next_path");
    props.history.push(targetPath);
  };

  const manageLogin = (e) => {
    e.preventDefault();
    setSubmit(true);

    let data = {
      mobile_number: props.phone,
      otp_value: OTP,
    };
    setIsActive(true);
    verifyOtp(data)
      .then((res) => {
        if (res.multiple_records_exist) {
          setRecords(res.records || []);
          setTempToken(res.temp_token);
          setShowSelection(true);
          setIsActive(false);
          setSubmit(false);
        } else {
          completeLogin(res.token);
        }
      })
      .catch((err) => {
        setSubmit(false);
        setIsActive(false);
        setSnackMessage(err.response?.data?.message || "Verification failed");
        setIsSnackError(true);
        setSnackOpen(true);
      });
  };

  const handleSelectApplication = (record) => {
    setIsActive(true);
    const data = {
      application_id: record.type === "application" ? record.id : null,
      company_id: record.type === "company" ? record.id : null,
    };

    selectApplicationContext(data, tempToken)
      .then((res) => {
        if (res.success) {
          if (res.context) {
            localStorage.setItem("context", JSON.stringify(res.context));
          }
          completeLogin(res.token);
        } else {
          setIsActive(false);
          setSnackMessage(res.message || "Failed to select application");
          setIsSnackError(true);
          setSnackOpen(true);
        }
      })
      .catch((err) => {
        setIsActive(false);
        setSnackMessage(err.response?.data?.message || "Selection error");
        setIsSnackError(true);
        setSnackOpen(true);
      });
  };

  const resend = (e) => {
    e.preventDefault();
    let loginParam = { mobile_number: props.phone };
    setSubmit(true);
    setIsActive(true);

    userLogin(loginParam)
      .then((res) => {
        setSnackMessage(res.message);
        setSubmit(false);
        setIsActive(false);
        setIsSnackError(false);
        setSnackOpen(true);
      })
      .catch((err) => {
        setSubmit(false);
        setSnackMessage(err.response?.data?.message || "Failed to resend OTP");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsActive(false);
      });
  };

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!flag) {
      setOTP("");
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      0: "Draft",
      1: "Submitted",
      2: "Under Review",
      3: "Approved",
      4: "Rejected",
      7: "In Progress",
    };
    return labels[status] || "Unknown";
  };

  const RecordIcon = ({ record }) => {
    // If company has a logo, use it
    if (record.logo) {
      const servInstance = process.env.REACT_APP_BASE_URL;
      const result = servInstance.substring(0, servInstance?.length - 4);
      return (
        <div className="record-logo-wrapper">
          <img src={`${result}${record.logo}`} alt={record.company_name} onError={(e) => e.target.style.display = 'none'} />
        </div>
      );
    }

    // Default avatars based on first letter of company name
    const firstLetter = record.company_name?.charAt(0).toUpperCase() || "?";
    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
    const colorIndex = firstLetter.charCodeAt(0) % colors.length;
    
    return (
      <div className="record-avatar" style={{ background: colors[colorIndex] }}>
        <span>{firstLetter}</span>
      </div>
    );
  };

  const getApplicationTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "kerala":
        return (
          <div className="type-icon-small kerala-bg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            </svg>
          </div>
        );
      case "india":
        return (
          <div className="type-icon-small india-bg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
        );
      default:
        return (
          <div className="type-icon-small generic-bg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="otp-login-container">
      <Loader open={isActive} />
      <div className={`otp-login-section ${showSelection ? "selection-active" : ""}`}>
        {!showSelection ? (
          <div>
            <form onSubmit={manageLogin}>
              <div className="otp-login-body d-flex justify-content-center align-items-center flex-column">
                <div>
                  <img src="/resources/img/login/login-avatar.png" alt="" />
                </div>
                <div className="otp-number-label">Enter the OTP received in your Number</div>
                <OTPInput value={OTP} onChange={setOTP} autoFocus OTPLength={6} otpType="number" disabled={false} />
              </div>
              <div className="otp-login-action">
                <button disabled={submit || OTP.length < 6} className="otp-login-button">
                  Verify
                </button>
              </div>
            </form>
            <div className="d-flex justify-content-around">
              <div className="otp-not-text" onClick={() => props.history.push("/login")}>
                Re-enter your number
              </div>
              <div className="otp-resend-text" onClick={(e) => resend(e)}>
                Resend OTP
              </div>
            </div>
          </div>
        ) : (
          <div className="selection-body premium-selection">
            <div className="selection-header-premium">
              <div className="welcome-badge">WELCOME BACK</div>
              <h2>Select Your Account</h2>
              <p>We found multiple records associated with <b>{props.phone}</b>. Please choose one to proceed to your dashboard.</p>
            </div>
            <div className="records-grid-glass">
              {records.map((record, index) => (
                <div key={index} className="glass-card animated-card" onClick={() => handleSelectApplication(record)}>
                  <div className="glass-card-header">
                    <div className="glass-icon-wrapper">
                      <RecordIcon record={record} />
                      {getApplicationTypeIcon(record.type)}
                    </div>
                    {/* <span className={`glass-pill status-${record.application_status}`}>
                      {getStatusLabel(record.application_status)}
                    </span> */}
                  </div>
                  <div className="glass-card-body">
                    <h3 className="company-name-glass">{record.company_name}</h3>
                    <div className="glass-ref-row">
                      <span className="glass-label">REF ID</span>
                      <span className="glass-value">{record.unique_application_ref_no || `ID: ${record.id}`}</span>
                    </div>
                  </div>
                  <div className="glass-card-footer">
                    <div className="glass-type-tag">
                      {getApplicationDisplayName(record)}
                    </div>
                    <div className="glass-action">
                      <span>Login</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {records.length > 4 && (
              <div className="scroll-hint-glass">
                <span>Scroll for more</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
                </svg>
              </div>
            )}
            <div className="selection-footer-premium">
              <button className="btn-back-minimal" onClick={() => setShowSelection(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Change Number
              </button>
            </div>
          </div>
        )}
      </div>
      <Snackbar message={snackMessage} error={isSnackError} open={snackOpen} close={snackbarClose} />
    </div>
  );
}
